import React, { Component } from 'react';
import Button from '@material-ui/core/Button';
import SearchIcon from '@material-ui/icons/Search';
import withHelpDrawer from './components/withHelpDrawer.jsx';
import ConcordanceCard from './components/Cards/ConcordanceCard';
import { SourcePermissions } from './components/Core/TextComponents';
import { ThemeContext } from './components/Core/ThemeContext';
import SEO from './components/SEO';
import { buildCanonicalUrl } from './utilities/canonicalUrls';
import { perspectives } from './data/perspectives';
import plans from './plans/plans.json';
import { buildPlansPath } from './utilities/planPaths';
import { perspectiveMatchesQuery } from './utils/perspectiveSearch';
import './bible-search.css';

const PAGE_SIZE = 30;

const searchFilters = [
    { text: 'Old Testament', filter: '$oldTestament', group: 'Testament' },
    { text: 'New Testament', filter: '$newTestament', group: 'Testament' },
    { text: 'Gospels & Acts', filter: '$gospelActs', group: 'Books' },
    { text: 'Wisdom', filter: '$wisdom', group: 'Books' },
    { text: 'Prophets', filter: '$prophets', group: 'Books' },
];

const suggestedSearches = ['love your enemies', 'faith hope', 'forgiveness', 'wisdom'];

const normalizeText = (value = '') => `${value}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const matchesAllTerms = (text, query) => {
    const searchable = normalizeText(text);
    const terms = normalizeText(query).split(' ').filter(Boolean);
    return terms.length > 0 && terms.every((term) => searchable.includes(term));
};

const titleMatchScore = (title, query) => {
    const normalizedTitle = normalizeText(title);
    const normalizedQuery = normalizeText(query);
    if (normalizedTitle === normalizedQuery) return 3;
    if (normalizedTitle.startsWith(normalizedQuery)) return 2;
    if (normalizedTitle.includes(normalizedQuery)) return 1;
    return 0;
};

let entityIndexesPromise;
const loadEntityIndexes = () => {
    if (!entityIndexesPromise) {
        entityIndexesPromise = Promise.all(['/data/people-index.json', '/data/places-index.json'].map((url) =>
            fetch(url).then((response) => {
                if (!response.ok) throw new Error('Could not load search indexes.');
                return response.json();
            })
        )).then(([people, places]) => ({ people, places })).catch((error) => {
            entityIndexesPromise = null;
            throw error;
        });
    }
    return entityIndexesPromise;
};

const findContentResults = async (query) => {
    const entityIndexes = await loadEntityIndexes();
    const matchedPerspectives = perspectives
        .filter((topic) => perspectiveMatchesQuery(topic, query))
        .sort((first, second) => titleMatchScore(second.shortTitle || second.question, query) - titleMatchScore(first.shortTitle || first.question, query))
        .slice(0, 8);
    const matchedPlans = Object.entries(plans)
        .filter(([title, plan]) => matchesAllTerms([
            title, plan.summary,
            ...((plan.stages || []).flatMap((stage) => [stage.title, ...((stage.passages || []).flatMap((passage) => [
                passage.title, passage.commentary, passage.application, passage.meditation,
            ]))])),
        ].join(' '), query))
        .sort(([first], [second]) => titleMatchScore(second, query) - titleMatchScore(first, query))
        .slice(0, 8);
    const findEntities = (entities) => entities
        .filter((entity) => matchesAllTerms(`${entity.name} ${entity.meaning || ''}`, query))
        .sort((first, second) => titleMatchScore(second.name, query) - titleMatchScore(first.name, query))
        .slice(0, 8);

    return {
        perspectives: matchedPerspectives,
        devotionals: matchedPlans,
        people: findEntities(entityIndexes.people),
        places: findEntities(entityIndexes.places),
    };
};

const normalizeGroup = (value = '') => `${value}`.replace(/([A-Z])/g, ' $1').trim();

const toSearchResult = (element) => ({
    value: element.Verse,
    bookname: element.Book,
    summary: element.Text || '',
    summaryExt: element.TextExt || '',
    verse: element.Verse,
    subgroup: normalizeGroup(element.Subgroup) === 'Gospel And Acts' ? 'The Gospels & Acts' : normalizeGroup(element.Subgroup),
    grouping: element.Grouping || '',
    book: element.Book || '',
    bookcode: element.BookCode || '',
    osisref: element.OsisRef || '',
});

const validateWebMcpInput = (input) => {
    if (!input || typeof input !== 'object' || Array.isArray(input)) {
        throw new Error('Provide a search query.');
    }
    const query = typeof input.query === 'string' ? input.query.trim() : '';
    if (!query) throw new Error('The query must be a non-empty string.');
    if (query.length > 200) throw new Error('The query must be 200 characters or fewer.');
    const filters = input.filters == null ? [] : input.filters;
    if (!Array.isArray(filters) || filters.some((filter) => !searchFilters.some((allowed) => allowed.filter === filter))) {
        throw new Error('One or more filters are not supported.');
    }
    return { query, filters };
};

const HelpPage = () => (
    <div>
        <h1>Search the Bible</h1>
        <p>Search for a word or phrase. Add a filter to narrow results by testament or biblical book group.</p>
    </div>
);

class ReadingListSearch extends Component {
    static contextType = ThemeContext;

    constructor(props) {
        super(props);
        this.state = {
            query: '',
            activeFilters: [],
            results: [],
            contentResults: { perspectives: [], devotionals: [], people: [], places: [] },
            contentLoading: false,
            concordance: { entries: {}, sources: [] },
            sources: [],
            loading: false,
            hasSearched: false,
            hasMore: false,
            offset: 0,
            error: '',
        };
        this.requestId = 0;
        this.webMcpLifecycle = null;
    }

    componentDidMount() {
        this.context.loading(false);
        this.registerWebMcpTools();

        const params = new URLSearchParams(window.location.search);
        const query = params.get('query') || '';
        if (query.trim()) {
            this.setState({ query }, () => {
                this.performSearch(query, [], { updateUrl: false }).catch(() => {});
            });
        }
    }

    componentWillUnmount() {
        this.requestId += 1;
        if (this.webMcpLifecycle) this.webMcpLifecycle.abort();
    }

    registerWebMcpTools = () => {
        const registry = typeof document === 'undefined' ? null : document.modelContext;
        if (typeof registry?.registerTool !== 'function') return;

        const lifecycle = new AbortController();
        this.webMcpLifecycle = lifecycle;
        const thisPage = this;

        try {
            Promise.resolve(registry.registerTool({
                name: 'search_bible',
                title: 'Search the Bible',
                description: 'Search Bible verses by word or phrase, optionally narrowing results to a testament or book group. Runs the same search shown on the page and returns the displayed matches.',
                inputSchema: {
                    type: 'object',
                    properties: {
                        query: { type: 'string', minLength: 1, maxLength: 200, description: 'A word or phrase to find in Bible verses.' },
                        filters: {
                            type: 'array',
                            uniqueItems: true,
                            items: { type: 'string', enum: searchFilters.map((filter) => filter.filter) },
                            description: 'Optional filters: $oldTestament, $newTestament, $gospelActs, $wisdom, or $prophets.',
                        },
                    },
                    required: ['query'],
                    additionalProperties: false,
                },
                annotations: { readOnlyHint: true, openWorldHint: false, untrustedContentHint: true },
                async execute(input) {
                    const { query, filters } = validateWebMcpInput(input);
                    const result = await thisPage.performSearch(query, filters);
                    return {
                        query,
                        resultCount: result.results.length,
                        hasMore: result.hasMore,
                        results: result.results.map((verse) => ({
                            reference: verse.verse,
                            text: verse.summary,
                            bookGroup: verse.grouping,
                            bookType: verse.subgroup,
                            url: `${window.location.origin}${window.getVerseURL(verse.osisref)}`,
                        })),
                    };
                },
            }, { signal: lifecycle.signal })).catch((error) => {
                if (!lifecycle.signal.aborted) console.warn('Could not register Bible search WebMCP tool.', error);
            });

            Promise.resolve(registry.registerTool({
                name: 'search_ai_bible',
                title: 'Search ai-BIBLE',
                description: 'Search the visible ai-BIBLE search page across Bible verses, perspectives, devotional plans, people, and places.',
                inputSchema: {
                    type: 'object',
                    properties: { query: { type: 'string', minLength: 1, maxLength: 200, description: 'A word or phrase to search across ai-BIBLE.' } },
                    required: ['query'],
                    additionalProperties: false,
                },
                annotations: { readOnlyHint: true, openWorldHint: false, untrustedContentHint: true },
                async execute(input) {
                    const { query } = validateWebMcpInput({ query: input && input.query, filters: [] });
                    const result = await thisPage.searchAll(query);
                    return result;
                },
            }, { signal: lifecycle.signal })).catch((error) => {
                if (!lifecycle.signal.aborted) console.warn('Could not register ai-BIBLE search WebMCP tool.', error);
            });
        } catch (error) {
            console.warn('Could not register Bible search WebMCP tool.', error);
        }
    }

    setQuery = (query) => {
        this.setState({ query });
    }

    updateSearchUrl = (query) => {
        const nextUrl = query ? `/search?query=${encodeURIComponent(query)}` : '/search';
        window.history.replaceState({}, '', nextUrl);
    }

    toggleFilter = (filter) => {
        this.setState((state) => ({
            activeFilters: state.activeFilters.includes(filter)
                ? state.activeFilters.filter((item) => item !== filter)
                : [...state.activeFilters, filter],
        }), () => {
            if (this.state.hasSearched && this.state.query.trim()) {
                this.performSearch(this.state.query, this.state.activeFilters).catch(() => {});
            }
        });
    }

    fetchSearchPage = async (query, offset) => {
        const { token } = localStorage;
        let endpoint = 'http://localhost:5001/v1/data/search';
        if (process.env.REACT_APP_AWS_API || !window.web_version) {
            endpoint = `${window.API_BASE}/v1/data/search`;
        }

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token || ''}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ search: [query], startResult: offset, maxResults: PAGE_SIZE }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.error || data.Error) {
            throw new Error(data.error || data.Error || `Search failed (${response.status}).`);
        }
        return data;
    }

    filterResults = (results, filters) => {
        if (!filters.length) return results;
        return results.filter((result) => filters.some((filter) => {
            if (filter === '$newTestament') return result.grouping === 'New Testament';
            if (filter === '$oldTestament') return result.grouping === 'Old Testament';
            if (filter === '$wisdom') return result.subgroup === 'Wisdom';
            if (filter === '$gospelActs') return result.subgroup === 'The Gospels & Acts';
            if (filter === '$prophets') return ['Minor Prophets', 'Major Prophets'].includes(result.subgroup);
            return false;
        }));
    }

    searchAll = async (query) => {
        const contentResults = await findContentResults(query);
        const bible = await this.performSearch(query, []);
        return {
            query,
            bible: bible.results.map((verse) => ({ reference: verse.verse, text: verse.summary })),
            perspectives: contentResults.perspectives.map((topic) => ({ title: topic.shortTitle || topic.question, url: `${window.location.origin}/perspectives/${topic.slug}` })),
            devotionals: contentResults.devotionals.map(([title, plan]) => ({ title, summary: plan.summary, url: `${window.location.origin}${buildPlansPath(title)}` })),
            people: contentResults.people.map((person) => ({ title: person.name, url: `${window.location.origin}/person/${person.ref}` })),
            places: contentResults.places.map((place) => ({ title: place.name, url: `${window.location.origin}/location/${place.ref}` })),
        };
    }

    performSearch = async (query = this.state.query, filters = this.state.activeFilters, options = {}) => {
        const normalizedQuery = `${query || ''}`.trim();
        const normalizedFilters = Array.isArray(filters) ? filters : [];
        const append = Boolean(options.append);
        if (!normalizedQuery) {
            this.setState({ error: 'Enter a word or phrase to search the Bible.' });
            return { results: [], hasMore: false };
        }

        const requestId = ++this.requestId;
        const offset = append ? this.state.offset : 0;
        if (options.updateUrl !== false) this.updateSearchUrl(normalizedQuery);
        this.setState({
            query: normalizedQuery,
            activeFilters: normalizedFilters,
            loading: true,
            error: '',
            ...(append ? {} : { results: [], concordance: { entries: {}, sources: [] }, hasSearched: true, offset: 0 }),
            ...(append ? {} : { contentResults: { perspectives: [], devotionals: [], people: [], places: [] } }),
            ...(append ? {} : { contentLoading: true }),
        });

        if (!append) {
            findContentResults(normalizedQuery).then((contentResults) => {
                if (requestId === this.requestId) this.setState({ contentResults, contentLoading: false });
            }).catch((contentError) => {
                console.warn('Could not load searchable ai-BIBLE content.', contentError);
                if (requestId === this.requestId) this.setState({ contentLoading: false });
            });
        }

        try {
            const data = await this.fetchSearchPage(normalizedQuery, offset);
            if (requestId !== this.requestId) return { results: [], hasMore: false };
            const fetched = (Array.isArray(data.results) ? data.results : []).map(toSearchResult);
            const filtered = this.filterResults(fetched, normalizedFilters);
            const hasMore = fetched.length === PAGE_SIZE;
            const results = await new Promise((resolve) => {
                this.setState((state) => ({
                    results: append ? [...state.results, ...filtered] : filtered,
                    concordance: data.concordance || { entries: {}, sources: [] },
                    sources: data.sources || [],
                    hasSearched: true,
                    hasMore,
                    offset: offset + fetched.length,
                    loading: false,
                }), () => resolve(append ? [...this.state.results] : filtered));
            });
            return { results, hasMore };
        } catch (error) {
            if (requestId === this.requestId) {
                const message = error instanceof TypeError && /fetch/i.test(error.message)
                    ? 'Could not connect to the Bible search service. Check your connection and try again.'
                    : error.message || 'Search could not be completed.';
                this.setState({ loading: false, hasSearched: true, error: message });
            }
            throw error;
        }
    }

    handleSubmit = (event) => {
        if (event) event.preventDefault();
        this.performSearch().catch(() => {});
    }

    handleLoadMore = () => {
        this.performSearch(this.state.query, this.state.activeFilters, { append: true }).catch(() => {});
    }

    selectSuggestion = (query) => {
        this.setState({ query }, () => this.performSearch(query).catch(() => {}));
    }

    render() {
        const HelpDrawer = withHelpDrawer(HelpPage);
        const { query, activeFilters, results, contentResults, contentLoading, loading, hasSearched, hasMore, error, concordance, sources } = this.state;
        const contentCount = Object.values(contentResults).reduce((count, items) => count + items.length, 0);
        const canonical = buildCanonicalUrl('/search');
        document.title = 'Search the Bible | ai-BIBLE';

        return (
            <main className="bible-search-page">
                <SEO
                    canonical={canonical}
                    title="Search the Bible | ai-BIBLE"
                    description="Search the Bible by word or phrase, browse verse matches, and narrow results by testament and biblical book group."
                />
                <HelpDrawer />
                <div className="bible-search-frame">
                    <header className="bible-search-hero">
                        <span className="bible-search-hero-strip" aria-hidden="true" />
                        <div className="bible-search-hero-copy">
                            <p className="bible-search-eyebrow"><SearchIcon aria-hidden="true" /> Search ai-BIBLE</p>
                            <h1>Find a word. Follow its story.</h1>
                            <p>Search Scripture and explore related perspectives, devotionals, people, and places.</p>
                            <a className="bible-search-webmcp-link" href="/web-mcp-support">Web MCP Support <span aria-hidden="true">→</span></a>
                        </div>
                    </header>

                    <section className="bible-search-panel" aria-label="Bible search">
                        <form onSubmit={this.handleSubmit}>
                            <label htmlFor="bible-search-query">What would you like to find?</label>
                            <div className="bible-search-input-row">
                                <input
                                    id="bible-search-query"
                                    type="search"
                                    value={query}
                                    maxLength={200}
                                    onChange={(event) => this.setQuery(event.target.value)}
                                    placeholder="Try “love your enemies” or “faith hope”"
                                    aria-describedby="bible-search-hint"
                                />
                                <Button type="submit" variant="contained" color="primary" disabled={loading || !query.trim()}>
                                    <SearchIcon aria-hidden="true" /> <span>{loading ? 'Searching…' : 'Search all'}</span>
                                </Button>
                            </div>
                            <p id="bible-search-hint" className="bible-search-hint">Search verses and ai-BIBLE resources by title, name, or related content.</p>
                        </form>

                        <div className="bible-search-suggestions" aria-label="Suggested searches">
                            <span>Try:</span>
                            {suggestedSearches.map((suggestion) => (
                                <button key={suggestion} type="button" onClick={() => this.selectSuggestion(suggestion)}>{suggestion}</button>
                            ))}
                        </div>

                        <fieldset className="bible-search-filters">
                            <legend>Narrow your results</legend>
                            <div className="bible-search-filter-groups">
                                {['Testament', 'Books'].map((group) => (
                                    <div className="bible-search-filter-group" key={group}>
                                        <span>{group}</span>
                                        {searchFilters.filter((filter) => filter.group === group).map((filter) => {
                                            const checked = activeFilters.includes(filter.filter);
                                            return (
                                                <button
                                                    key={filter.filter}
                                                    className={checked ? 'is-active' : ''}
                                                    type="button"
                                                    aria-pressed={checked}
                                                    onClick={() => this.toggleFilter(filter.filter)}
                                                >
                                                    {filter.text}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ))}
                                {activeFilters.length ? (
                                    <button className="bible-search-clear-filters" type="button" onClick={() => this.setState({ activeFilters: [] }, () => {
                                        if (hasSearched) this.performSearch(query, []).catch(() => {});
                                    })}>Clear filters</button>
                                ) : null}
                            </div>
                        </fieldset>
                    </section>

                    {hasSearched ? (
                        <section className="bible-search-results" aria-label="Bible search results" aria-busy={loading}>
                            <div className="bible-search-results-heading">
                                <div>
                                    <p className="bible-search-eyebrow">Results</p>
                                <h2>{results.length + contentCount ? `${results.length + contentCount} results found` : 'Search results'}</h2>
                                </div>
                                <span>“{query}”{activeFilters.length ? ` · ${activeFilters.length} filter${activeFilters.length === 1 ? '' : 's'}` : ''}</span>
                            </div>

                            {error ? <div className="bible-search-message is-error" role="alert">{error}</div> : null}
                            {loading && !results.length && !contentCount ? <div className="bible-search-message" role="status">Searching Scripture and ai-BIBLE resources…</div> : null}
                            {!loading && !contentLoading && !error && !results.length && !contentCount ? (
                                <div className="bible-search-message">
                                    <h3>No results found</h3>
                                    <p>Try a shorter word or a different phrase, or remove one of the Bible filters.</p>
                                </div>
                            ) : null}

                            {[
                                { key: 'perspectives', title: 'Perspectives', items: contentResults.perspectives.map((topic) => ({ title: topic.shortTitle || topic.question, summary: topic.summary, href: `/perspectives/${topic.slug}` })) },
                                { key: 'devotionals', title: 'Devotional plans', items: contentResults.devotionals.map(([title, plan]) => ({ title, summary: plan.summary, href: buildPlansPath(title) })) },
                                { key: 'people', title: 'People', items: contentResults.people.map((person) => ({ title: person.name, summary: person.meaning || `${person.verseCount} biblical references`, href: `/person/${person.ref}`, meta: `${person.verseCount} references` })) },
                                { key: 'places', title: 'Places', items: contentResults.places.map((place) => ({ title: place.name, summary: place.meaning || `${place.verseCount} biblical references`, href: `/location/${place.ref}`, meta: `${place.verseCount} references` })) },
                            ].map((section) => section.items.length ? (
                                <section className={`bible-search-content-section bible-search-content-${section.key}`} aria-label={`${section.title} results`} key={section.key}>
                                    <div className="bible-search-content-heading"><h3>{section.title}</h3><span>{section.items.length}{section.items.length === 8 ? '+' : ''}</span></div>
                                    <div className="bible-search-content-grid">
                                        {section.items.map((item, index) => (
                                            <a className="bible-search-content-card" href={item.href} key={`${item.href}-${index}`}>
                                                <strong>{item.title}</strong>
                                                {item.summary ? <span>{item.summary}</span> : null}
                                                {item.meta ? <small>{item.meta}</small> : null}
                                            </a>
                                        ))}
                                    </div>
                                </section>
                            ) : null)}

                            {Object.keys(concordance.entries || {}).length ? (
                                <ConcordanceCard sources={concordance.sources} sentence={concordance.entries} />
                            ) : null}

                            <div className="bible-search-result-list">
                                {results.map((result) => (
                                    <article className="bible-search-result" key={result.osisref}>
                                        <div className="bible-search-result-meta">
                                            <span>{result.grouping}</span>
                                            {result.subgroup ? <span>{result.subgroup}</span> : null}
                                        </div>
                                        <h3>{result.verse}</h3>
                                        <p className="bible-search-result-text">{result.summary}</p>
                                        <a className="bible-search-read-link" href={window.getVerseURL(result.osisref)}>Read in context <span aria-hidden="true">→</span></a>
                                    </article>
                                ))}
                            </div>

                            {hasMore ? (
                                <div className="bible-search-load-more-wrap">
                                    <button type="button" className="bible-search-load-more" onClick={this.handleLoadMore} disabled={loading}>
                                        {loading ? 'Loading…' : 'Load more verses'}
                                    </button>
                                </div>
                            ) : null}
                            {sources.length ? <div className="bible-search-sources"><SourcePermissions sources={sources} /></div> : null}
                        </section>
                    ) : (
                        <section className="bible-search-start" aria-label="Search tips">
                        <h2>Search across Scripture and ai-BIBLE resources</h2>
                            <p>Find Bible verses, perspectives, devotional plans, people, and places from one search. Testament and book filters apply to verse results.</p>
                        </section>
                    )}
                </div>
            </main>
        );
    }
}

export default ReadingListSearch;
